/**
 * Attribution tracking utility
 * Captures UTM parameters and referrer information from URL into localStorage
 * - First attribution is set once and never overwritten
 * - Last attribution updates whenever new UTMs appear
 */

import { EMPTY_STRING } from '../constants/constant';
import { TrackingParam } from '../constants/trackingParams';

export const ATTRIB_STORAGE_KEY = 'assetron_attrib_v1';

/**
 * Complete attribution data object captured from a URL or interaction
 * All instances will have landing_url and ts_iso populated
 */
export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  /** Landing page URL where attribution was captured - always present */
  landing_url: string;
  /** ISO timestamp of when attribution was captured - always present */
  ts_iso: string;
}

/**
 * Container for first-touch and last-touch attribution data
 *
 * Type Guarantees:
 * - If `first` is defined, it will always be a complete AttributionData object
 *   with landing_url and ts_iso populated
 * - If `last` is defined, it will always be a complete AttributionData object
 *   with landing_url and ts_iso populated
 * - Both can be undefined if no attribution has been captured yet
 */
export interface StoredAttribution {
  /** First-touch attribution (captured on first visit with UTM parameters) */
  first?: AttributionData;
  /** Last-touch attribution (captured on latest visit with UTM parameters) */
  last?: AttributionData;
}

/**
 * Reads a query parameter from the current URL
 */
function readQueryParam(name: string): string | undefined {
  // Guard against non-browser environments
  if (typeof globalThis === 'undefined' || !globalThis.location) {
    return undefined;
  }
  const paramValue = new URLSearchParams(globalThis.location.search).get(name);
  // Use nullish coalescing to only convert null to undefined, preserving empty strings
  return paramValue ?? undefined;
}

/**
 * Returns current ISO timestamp
 */
function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Retrieves stored attribution data from localStorage
 */
function getAttribution(): StoredAttribution {
  // Guard against non-browser environments
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
    return {};
  }
  try {
    return JSON.parse(localStorage.getItem(ATTRIB_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Saves attribution data to localStorage
 */
function setAttribution(data: StoredAttribution): void {
  // Guard against non-browser environments
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
    return;
  }

  try {
    localStorage.setItem(ATTRIB_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving attribution data to localStorage:', error);
    // Gracefully handle localStorage errors:
    // - Storage quota exceeded (QuotaExceededError)
    // - Private browsing mode where localStorage is unavailable
    // - Browser settings that deny localStorage access
    // - SecurityError in cross-origin scenarios
    // Silently ignore to prevent breaking the application
  }
}

/**
 * Captures attribution data from current URL and stores it in localStorage
 *
 * Behavior:
 * - First visit WITH UTMs: Sets both "first" and "last" to the same attribution data
 * - Second visit WITH new UTMs: Keeps "first" unchanged, updates "last" with new UTMs
 * - Second visit WITHOUT UTMs: Does nothing - keeps both "first" and "last" unchanged
 * - ts_iso (timestamp) only updates when attribution data actually changes
 *
 * Summary:
 * - "first" = First-touch attribution (never changes once set)
 * - "last" = Last-touch attribution (only updates when new UTMs appear)
 * - "ts_iso" = Timestamp (only updates when attribution data changes)
 * - No UTMs = No change (return early)
 *
 * Should be called on every page load, especially marketing/auth pages
 */
export function captureAttributionFromUrl(): void {
  // Guard against non-browser environments (SSR, Node.js, testing)
  if (
    typeof globalThis === 'undefined' ||
    !globalThis.location ||
    !globalThis.localStorage
  ) {
    return;
  }

  // Check if there are any tracking parameters in the URL
  const params = new URLSearchParams(globalThis.location.search);
  const hasAnyTrackingParams = !!(
    params.get(TrackingParam.UTM_SOURCE) ||
    params.get(TrackingParam.UTM_MEDIUM) ||
    params.get(TrackingParam.UTM_CAMPAIGN) ||
    params.get(TrackingParam.UTM_TERM) ||
    params.get(TrackingParam.UTM_CONTENT) ||
    params.get(TrackingParam.GCLID) ||
    params.get(TrackingParam.FBCLID)
  );

  if (!hasAnyTrackingParams) {
    return; // No tracking parameters, do nothing
  }

  // Build UTM object without timestamp first (for comparison)
  const newUtmData = {
    utm_source: readQueryParam(TrackingParam.UTM_SOURCE),
    utm_medium: readQueryParam(TrackingParam.UTM_MEDIUM),
    utm_campaign: readQueryParam(TrackingParam.UTM_CAMPAIGN),
    utm_term: readQueryParam(TrackingParam.UTM_TERM),
    utm_content: readQueryParam(TrackingParam.UTM_CONTENT),
    gclid: readQueryParam(TrackingParam.GCLID),
    fbclid: readQueryParam(TrackingParam.FBCLID),
    referrer: document.referrer || undefined,
    landing_url: globalThis.location.href,
  };

  const storedAttribution = getAttribution();

  // Helper function to check if two attribution objects have same tracking data
  const isSameTracking = (
    a: Partial<AttributionData>,
    b: Partial<AttributionData>,
  ): boolean => {
    return (
      a.utm_source === b.utm_source &&
      a.utm_medium === b.utm_medium &&
      a.utm_campaign === b.utm_campaign &&
      a.utm_term === b.utm_term &&
      a.utm_content === b.utm_content &&
      a.gclid === b.gclid &&
      a.fbclid === b.fbclid &&
      a.referrer === b.referrer &&
      a.landing_url === b.landing_url
    );
  };

  // Helper function to create attribution data with current timestamp
  const createAttributionWithTimestamp = (): AttributionData => ({
    ...newUtmData,
    ts_iso: nowIso(),
  });

  // Set first attribution only once
  if (!storedAttribution.first) {
    const utm = createAttributionWithTimestamp();
    storedAttribution.first = utm;
    storedAttribution.last = utm;
    setAttribution(storedAttribution);
  } else if (!isSameTracking(newUtmData, storedAttribution.last || {})) {
    // Only update last if tracking data is different from current last
    storedAttribution.last = createAttributionWithTimestamp();
    setAttribution(storedAttribution);
  }
}

/**
 * Retrieves the stored attribution data
 */
export function getStoredAttribution(): StoredAttribution {
  return getAttribution();
}

/**
 * Clears all stored attribution data (useful for testing or manual reset)
 */
export function clearAttribution(): void {
  // Guard against non-browser environments
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
    return;
  }
  try {
    localStorage.removeItem(ATTRIB_STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error clearing attribution data from localStorage:', error);
    // Gracefully handle localStorage errors
    // Silently ignore to prevent breaking the application
  }
}

/**
 * Returns the first attribution data, if available
 */
export function getFirstAttribution(): AttributionData | undefined {
  return getAttribution().first;
}

/**
 * Returns the last attribution data, if available
 */
export function getLastAttribution(): AttributionData | undefined {
  return getAttribution().last;
}

/**
 * Generates a URL with first attribution UTM parameters appended as query string
 *
 * Useful for cross-domain redirects where localStorage is isolated by same-origin policy.
 * When redirecting from one domain to another, append the original UTM parameters to the URL
 * so they can be re-captured and stored in the new domain's localStorage.
 *
 * @param baseUrl The base URL to append UTM parameters to
 * @param skipExisting If true, skips adding UTM parameters that already exist in the URL
 * @returns URL with UTM parameters appended, or original baseUrl if no attribution data exists
 */
export function addAttributionToUrl(
  baseUrl: string,
  skipExisting = false,
): string {
  // Guard against non-browser environments
  if (typeof globalThis === 'undefined' || !globalThis.location) {
    return baseUrl;
  }

  const attribution = getAttribution();

  // Use first attribution data for URL parameters (most relevant for tracking)
  if (!attribution.first) {
    return baseUrl;
  }

  // Parse existing parameters from the base URL to check for duplicates
  const [baseUrlWithoutQuery, queryString] = baseUrl.split('?');
  const existingParams = new URLSearchParams(queryString || EMPTY_STRING);

  const params = new URLSearchParams();
  const firstAttrib = attribution.first;

  // UTM parameters mapping: attribute -> tracking param constant
  const utmParams: Array<[keyof AttributionData, string]> = [
    ['utm_source', TrackingParam.UTM_SOURCE],
    ['utm_medium', TrackingParam.UTM_MEDIUM],
    ['utm_campaign', TrackingParam.UTM_CAMPAIGN],
    ['utm_term', TrackingParam.UTM_TERM],
    ['utm_content', TrackingParam.UTM_CONTENT],
    ['gclid', TrackingParam.GCLID],
    ['fbclid', TrackingParam.FBCLID],
  ];

  // Add UTM parameters from first attribution if they exist and should be added
  utmParams.forEach(([attrKey, paramKey]) => {
    const value = firstAttrib[attrKey];
    if (value && (!skipExisting || !existingParams.has(paramKey))) {
      params.append(paramKey, value);
    }
  });

  if (!params.toString()) {
    return baseUrl;
  }

  // Reconstruct URL with both existing and new parameters
  const allParams = new URLSearchParams();

  // Add existing parameters first
  existingParams.forEach((value, key) => {
    allParams.append(key, value);
  });

  // Add new attribution parameters
  params.forEach((value, key) => {
    allParams.append(key, value);
  });

  return `${baseUrlWithoutQuery}?${allParams.toString()}`;
}
